(ns manage.adset-decision-tree-page.view.hook
  (:require [cljs-bean.core :refer [->clj]]
            [common.queries :refer [invalidate-queries use-query]]
            [common.resource.query.decision-tree :as queries]
            [common.util :as util]
            [manage.ad-variant.hooks :as ah]
            [manage.adset-decision-tree-page.event :as event]
            [manage.adset-decision-tree-page.node-library :as node-library]
            [manage.adset-decision-tree-page.property :refer [ad-variants-data-path
                                                              property-by-id]]
            [manage.adset-decision-tree-page.selection :as selection]
            [manage.adset-decision-tree-page.sub :as sub]
            [manage.adset-decision-tree-page.validation :as validation]
            [manage.adset.hooks :as adset.h]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [defhook]]
            [uix.re-frame :refer [use-subscribe]]))

(defhook use-selected-item-data-path [{:keys [selected-item data-path on-success]}]
  (let [node-id (selection/get-host-node-id selected-item)
        node-data (use-subscribe [::sub/node-data node-id])
        base-path (selection/get-selected-item-base-path node-data selected-item)
        data-path (cond->> data-path
                    (some? base-path) (into base-path))
        value (get-in node-data data-path)

        set-value
        (uix/use-callback
         (fn [new-value {set-value-on-success :on-success}]
           (let [updated-node (assoc-in node-data data-path new-value)
                 patch-data (select-keys updated-node [(first data-path)])]
             (rf/dispatch [::event/be-patch-node node-id patch-data
                           (or set-value-on-success on-success)])))
         [node-id on-success node-data data-path])]
    [value set-value]))

(defhook use-selected-item-property [{:keys [property-id] :as props}]
  (use-selected-item-data-path
   (assoc props :data-path (get-in property-by-id [property-id :data-path]))))

(defn clean-editor [{:keys [advertiser-id adset-id decision-tree-id]}]
  (rf/dispatch [::event/cleanup-editor])
  (invalidate-queries {:queryKey ["decision-tree" advertiser-id adset-id decision-tree-id]}))

(defhook use-editor-state [{:keys [read-only decision-tree-id]}]
  (let [{:keys [advertiser-id adset-id]} (use-subscribe [:url-context])
        {:keys [data isFetching] :as query-result}
        (use-query (queries/fetch-decision-tree-query
                    {:advertiser-id advertiser-id
                     :adset-id adset-id
                     :decision-tree-id decision-tree-id}))

        editor-is-initialized (use-subscribe [::sub/editor-is-initialized])]
    (uix/use-effect (fn [] #(clean-editor {:advertiser-id advertiser-id
                                           :adset-id adset-id
                                           :decision-tree-id decision-tree-id}))
                    [decision-tree-id adset-id advertiser-id])

    (uix/use-effect
     (fn []
       (when-not isFetching
         (rf/dispatch
          [::event/refresh-editor
           {:read-only read-only
            :decision-tree-id decision-tree-id
            :be-response (->clj data)}])))
     [data isFetching decision-tree-id read-only])

    (assoc query-result :editor-is-initialized editor-is-initialized)))

(defhook use-node-controls [{:keys [id type outcome-node-ids enable-refresh]
                             {:keys [disable-addition]} :outcome-properties}]
  (let [read-only (use-subscribe [::sub/editor-read-only])]
    (if read-only
      {}
      (cond-> {:on-duplicate (fn [_]
                               (rf/dispatch [::event/be-duplicate-node
                                             {:id id
                                              :select-after :decision-nodes}]))
               :on-delete (fn [_]
                            (let [delete-event [::event/delete-nodes
                                                {:node-ids [id]}]]
                              (if (seq outcome-node-ids)
                                (rf/dispatch
                                 [:modal/open
                                  {:id :decision-tree/confirm-node-delete
                                   :props {:entity-name (str "this "
                                                             (-> type
                                                                 node-library/type->node
                                                                 :name)
                                                             " Decision node")
                                           :on-submit delete-event}}])
                                (rf/dispatch delete-event))))}

        (not disable-addition)
        (assoc :on-add-outcome (fn [_]
                                 (rf/dispatch [::event/add-node-outcome
                                               {:id id :type type}])))

        enable-refresh
        (assoc :on-refresh (fn [_]
                             (rf/dispatch [::event/be-refresh-node
                                           {:id id}])))))))

(defhook use-node-outcome-control [{:keys [id disable-duplication]}]
  (uix/use-memo
   #(cond-> {:id id
             :on-select (fn [] (rf/dispatch [::event/select-nodes [id]]))
             :on-delete (fn [_e event-params]
                          (rf/dispatch [::event/delete-nodes
                                        (merge {:node-ids [id]} event-params)]))}
      (not disable-duplication)
      (assoc
       :on-duplicate (fn [_e]
                       (rf/dispatch [::event/be-duplicate-node
                                     {:id id
                                      :select-after :first-outcome}]))
       :on-duplicate-subtree (fn [_e]
                               (rf/dispatch [::event/be-duplicate-node
                                             {:id id
                                              :include-subtree true
                                              :select-after :decision-nodes}]))))
   [id disable-duplication]))

(defn upgrade-ad-variant
  [selected-ad-variants {:keys [newer-revisions ad-variant-uuid]}]
  (util/vec-swap selected-ad-variants
                 #(= (:ad-variant-uuid %) ad-variant-uuid)
                 (last (sub/get-ready-newer-revisions newer-revisions))))

(defhook use-upgrade-ad-variants
  [[value set-value] ad-variants]
  (let [ad-variants-to-upgrade (uix/use-memo #(sub/get-ad-variants-ready-for-update ad-variants)
                                             [ad-variants])
        upgrade-ad-variants
        (uix/use-callback
         #(set-value (reduce upgrade-ad-variant value ad-variants-to-upgrade)
                     {:on-success
                      (fn []
                        (queries/add-toaster
                         {:id (->> ad-variants-to-upgrade (map :uuid) (apply str))
                          :message "You have updated your Ad variants"}))})
         [ad-variants-to-upgrade value set-value])]
    (when (seq ad-variants-to-upgrade)
      upgrade-ad-variants)))

(defhook use-single-ad-variant-control
  [{:keys [selected-item state-controls ad-variant read-only]
    [value set-value] :state-controls
    {:keys [uuid]} :ad-variant}]
  {:ad-variant ad-variant
   :read-only read-only
   :on-configuration #(rf/dispatch
                       [::event/set-selection
                        [(assoc selected-item
                                :parent-type (:type selected-item)
                                :type :selection/variant
                                :variant-id uuid)]])
   :on-product-selector #(rf/dispatch
                          [::event/set-selection
                           [(assoc selected-item
                                   :parent-type (:type selected-item)
                                   :type :selection/product-selector
                                   :ad-variant ad-variant
                                   :variant-id uuid)]])
   :on-remove (fn [^js _event]
                (set-value (->> value
                                (remove (comp #{uuid} :uuid))
                                vec)))
   :on-upgrade (use-upgrade-ad-variants state-controls [ad-variant])})

(defhook use-grouped-ad-variant-control
  [{:keys [group-content-id ad-variants-in-group state-controls read-only]
    [value set-value] :state-controls}]
  {:read-only read-only
   :on-remove (uix/use-callback
               #(set-value (vec (remove (fn [{:keys [content-id]}]
                                          (= content-id group-content-id))
                                        value)))
               [group-content-id value set-value])
   :on-upgrade (use-upgrade-ad-variants state-controls ad-variants-in-group)})

(defhook use-ad-variant-controls
  [{:keys [selected-item single-default-per-dimensions]}]
  (let [current-channel (adset.h/use-channel)
        has-multiple-templates (ah/use-has-multiple-templates)

        [ad-variants set-ad-variants]
        (use-selected-item-data-path {:selected-item selected-item
                                      :data-path ad-variants-data-path})
        errors (validation/validate-variants ad-variants)
        on-upgrade (use-upgrade-ad-variants [ad-variants set-ad-variants] ad-variants)
        read-only (use-subscribe [::sub/editor-read-only])
        edit-variants
        (uix/use-callback
         (fn [^js _event]
           (rf/dispatch [:modal/open
                         {:id :decision-tree/select-ad-variants-modal
                          :props {:single-default-per-dimensions single-default-per-dimensions
                                  :selected-ad-variants ad-variants
                                  :set-ad-variants set-ad-variants
                                  :disable-grouping (= current-channel "meta")}}]))
         [single-default-per-dimensions ad-variants set-ad-variants current-channel])]
    (uix/use-memo
     (fn []
       (let [get-single-ad-variant-props
             (fn [{:keys [ad-variant-uuid uuid] :as ad-variant}]
               {:errors (get errors uuid)
                :selected-item selected-item
                :state-controls [ad-variants set-ad-variants]
                :ad-variant ad-variant
                :read-only read-only
                :key ad-variant-uuid})]
         {:edit-variants edit-variants
          :remove-all-variants #(set-ad-variants [])
          :on-upgrade on-upgrade
          :ad-variants ad-variants
          :any-variants-assigned (seq ad-variants)
          :has-multiple-templates has-multiple-templates
          :child-props
          (if has-multiple-templates
            (for [[group-content-id ad-variants-in-group] (group-by :content-id ad-variants)]
              {:key group-content-id
               :group-child-props (map get-single-ad-variant-props ad-variants-in-group)
               :group-content-id group-content-id
               :group-status (some (fn [{:keys [ad-variant-status]}]
                                     (when-not (= ad-variant-status "approved")
                                       ad-variant-status))
                                   ad-variants-in-group)
               :ad-variants-in-group ad-variants-in-group
               :state-controls [ad-variants set-ad-variants]
               :read-only read-only})
            (map get-single-ad-variant-props ad-variants))}))
     [has-multiple-templates ad-variants set-ad-variants selected-item errors edit-variants
      on-upgrade read-only])))

(defhook use-node-selected-item [node]
  (uix/use-memo #(when node (selection/get-selected-item-for-node node)) [node]))

(defhook use-node-ad-variant-controls [node]
  (use-ad-variant-controls {:selected-item (use-node-selected-item node)}))

(defhook use-channel-has-defaults []
  (let [current-channel (adset.h/use-channel)]
    (contains? node-library/channels-with-defaults current-channel)))

(defhook use-default-outcome-node-control [{decision-node-id :id}]
  (let [channel-has-defaults (use-channel-has-defaults)
        default-outcome-as-node (uix/use-memo (fn [] {:id decision-node-id
                                                      :category :default-outcome-node})
                                              [decision-node-id])
        selected-item (use-node-selected-item default-outcome-as-node)
        selected (use-subscribe [::sub/is-item-selected selected-item])
        {:keys [ad-variants edit-variants remove-all-variants]}
        (use-ad-variant-controls
         {:single-default-per-dimensions true
          :selected-item selected-item})

        on-select #(rf/dispatch [::event/set-selection [selected-item]])
        read-only (use-subscribe [::sub/editor-read-only])
        active (boolean (or selected (seq ad-variants)))]
    (when (and channel-has-defaults (or active (not read-only)))
      {:ad-variants ad-variants
       :active active
       :selected selected
       :on-select on-select
       :on-enable edit-variants
       :node default-outcome-as-node
       :on-disable (fn []
                     (remove-all-variants)
                     (rf/dispatch [::event/set-selection []]))})))
